// src/hooks/queries/useClientes.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientesService } from '@/services/clientes';
import type {
  Cliente,
  ClienteCreate,
  ClienteUpdate,
  Empresa,
  EmpresaCreate,
  Contacto,
  ContactoCreate,
  FilterParams,
  PaginatedResponse,
} from '@/types';
import { toast } from '@/components/ui';

interface ClienteFilters extends FilterParams {
  activo?: boolean;
}

// === CLIENTES ===

export function useClientes(params?: ClienteFilters) {
  return useQuery<PaginatedResponse<Cliente>>({
    queryKey: ['clientes', params],
    queryFn: () => clientesService.list(params),
  });
}

export function useCliente(id: string | undefined) {
  return useQuery<Cliente>({
    queryKey: ['clientes', id],
    queryFn: () => clientesService.get(id!),
    enabled: !!id,
  });
}

export function useCrearCliente() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ClienteCreate) => clientesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente creado correctamente');
    },

  });
}

export function useActualizarCliente() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClienteUpdate }) =>
      clientesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes', id] });
      toast.success('Cliente actualizado correctamente');
    },
  });
}

export function useEliminarCliente() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente eliminado correctamente');
    },
  });
}

// === EMPRESAS ===

export function useEmpresas(clienteId: string | undefined) {
  return useQuery<Empresa[]>({
    queryKey: ['clientes', clienteId, 'empresas'],
    queryFn: () => clientesService.getEmpresas(clienteId!),
    enabled: !!clienteId,
  });
}

export function useCrearEmpresa(clienteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmpresaCreate) => clientesService.createEmpresa(clienteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes', clienteId, 'empresas'] });
      toast.success('Empresa creada correctamente');
    },
  });
}

export function useEliminarEmpresa(clienteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (empresaId: string) => clientesService.deleteEmpresa(clienteId, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes', clienteId, 'empresas'] });
      toast.success('Empresa eliminada correctamente');
    },
  });
}

// === CONTACTOS ===

export function useContactos(clienteId: string | undefined) {
  return useQuery<Contacto[]>({
    queryKey: ['clientes', clienteId, 'contactos'],
    queryFn: () => clientesService.getContactos(clienteId!),
    enabled: !!clienteId,
  });
}

export function useCrearContacto(clienteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContactoCreate) => clientesService.createContacto(clienteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes', clienteId, 'contactos'] });
      toast.success('Contacto creado correctamente');
    },
  });
}

export function useEliminarContacto(clienteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactoId: string) => clientesService.deleteContacto(clienteId, contactoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes', clienteId, 'contactos'] });
      toast.success('Contacto eliminado correctamente');
    },
  });
}
